import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { buyersApi, type PrequalInviteDto } from "@/api/buyers";
import { StatusBadge, Timestamp, useToast } from "@/design-system/components/ui";
import { qualifiedAmountLabel } from "./prequalification/qualifiedAmount";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import {
  useBuyerPrequalificationAttempts,
  useBuyerPrequalificationInvites,
  useResendBuyerPrequalInvite,
  useSendBuyerPrequalInvite,
  useUnlinkBuyerPrequalification,
} from "@/hooks/buyers/usePrequalificationInvites";
import { PrequalificationCandidateModal } from "./PrequalificationCandidateModal";
import { DetailField, DetailFieldList } from "./detailBits";
import { useDateFormatter } from "@/utils/datetime";
import { phoneCertificationLine } from "@/lib/prequalCertification";
import { AttemptsSection } from "./prequalification/AttemptsSection";
import {
  InvitationsSection,
  OneClickInviteTrigger,
  SubjectDuplicateConfirm,
} from "./prequalification/InvitationsSection";
import { PrequalificationSurface } from "./prequalification/PrequalificationSurface";

/**
 * The buyer's Prequalification tab.
 *
 * <p>Three things a specialist does here: invite, review what came back, and
 * correct the link. Everything about the record's prequalification is on ONE
 * tab, and the milestone row on Overview is a read-only summary that links
 * across — one place to LOOK, one place to ACT.</p>
 *
 * <p>🔴 <b>A MOUNT, not an implementation.</b> The screen is
 * <c>PrequalificationSurface</c>, shared with the seller side. The two shipped
 * as separate implementations of the same screen and agreed on nothing but a
 * noun; this supplies the BUYER subject, the buyer's composer and the buyer's
 * attempts list.</p>
 *
 * <p>🔴 <b>The composer is deliberately NOT shared.</b> A buyer's address is on
 * their own record, so there is nothing to type and the invitation is one click.
 * The seller has no such record, so the address must be asked for and — being
 * hand-typed and unverified — goes through a preview first. Collapsing them
 * would mean either asking this surface for an address it already holds, or
 * letting an unverified one go out unreviewed.</p>
 */
export function PrequalificationTab({
  buyerLeadId,
  canEdit,
}: {
  buyerLeadId: string;
  canEdit: boolean;
}) {
  const toast = useToast();
  const [picking, setPicking] = useState(false);

  const link = useQuery({
    queryKey: buyerKeys.prequalification(buyerLeadId),
    queryFn: () => buyersApi.getPrequalification(buyerLeadId),
    enabled: Boolean(buyerLeadId),
  });
  const attempts = useBuyerPrequalificationAttempts(buyerLeadId);
  const unlink = useUnlinkBuyerPrequalification(buyerLeadId);

  const rows = useMemo(() => attempts.data ?? [], [attempts.data]);

  // Listing names keyed by the submission each attempt records, so a COMPLETED
  // invitation can show what it was sized against without a second read.
  const listingBySubmissionId = useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    // 🔴 The canonical label, not the raw business-name column. A listing whose
    // seller has not named the business has a headline, and printing the blank
    // column drops it out of the ladder every other surface walks.
    for (const a of rows) map[a.submissionId] = a.listingDisplayName ?? a.listingBusinessName;
    return map;
  }, [rows]);

  const l = link.data;

  return (
    <>
      <PrequalificationSurface
        subject="buyer"
        canEdit={canEdit}
        testId="prequalification-tab"
        status={{
          loading: link.isLoading,
          error: link.isError,
          retrying: link.isFetching,
          onRetry: () => void link.refetch(),
          linked: Boolean(l?.submissionId),
          body: l ? <LinkedBuyerFacts link={l} /> : null,
        }}
        link={{
          onChange: () => setPicking(true),
          onUnlink: () =>
            unlink.mutate(undefined, {
              onSuccess: () => toast.success("Prequalification unlinked."),
              onError: (err) =>
                toast.error(
                  (err as { message?: string })?.message
                    ?? "Could not unlink the prequalification."),
            }),
          unlinking: unlink.isPending,
          // Unlinking clears the POINTER and leaves the attempts standing — the
          // buyer did complete them, and erasing that history to record a
          // correction to the pointer would lose the more durable fact.
          footnote:
            "Unlinking clears the primary pointer only. The completed prequalifications "
            + "below stay on the record. Linking one this buyer completed before Phoenix "
            + "knew about them is the only way to attach it.",
        }}
        invitations={
          <BuyerInvitations
            buyerLeadId={buyerLeadId}
            canEdit={canEdit}
            listingBySubmissionId={listingBySubmissionId}
          />
        }
      >
        <AttemptsSection buyerLeadId={buyerLeadId} />
      </PrequalificationSurface>

      {/* REUSED, never reimplemented. Its discipline is the point: it surfaces
          when another buyer already claims a submission, requires a confirm,
          and never auto-links. */}
      <PrequalificationCandidateModal
        buyerLeadId={buyerLeadId}
        open={picking}
        onClose={() => setPicking(false)}
      />
    </>
  );
}

/** The linked prequalification's own facts. */
function LinkedBuyerFacts({
  link: l,
}: {
  link: NonNullable<Awaited<ReturnType<typeof buyersApi.getPrequalification>>>;
}) {
  const { date } = useDateFormatter();
  // Ruling Q4: a prequalification taken on a call says who certified it. The
  // date is an INSTANT, so it goes through the viewer's zone.
  const certification = phoneCertificationLine(l, date);
  return (
    <>
      <div className="mb-space-3 flex flex-wrap items-center gap-space-3">
        <StatusBadge tone={l.hasLetter ? "ok" : "warn"}>
          {l.hasLetter ? "Letter on file" : "No letter yet"}
        </StatusBadge>
        {!l.hasLetter && (
          <span className="text-caption text-gray-mid">
            A submission package embeds the letter, so it cannot be generated
            until one exists.
          </span>
        )}
      </div>

      <DetailFieldList>
        <DetailField label="Reference" value={l.referenceNumber} />
        <DetailField label="Outcome" value={l.outcome} />
        <DetailField label="Qualified buying power" value={qualifiedAmountLabel(l)} />
        <DetailField
          label="Submitted"
          value={l.submittedAtUtc ? <Timestamp value={l.submittedAtUtc} /> : null}
        />
        <DetailField
          label="Linked"
          value={l.linkedAtUtc ? <Timestamp value={l.linkedAtUtc} /> : null}
        />
      </DetailFieldList>

      {certification && (
        <p
          className="mt-space-3 mb-0 text-caption text-gray-mid"
          data-testid="prequal-phone-certification"
        >
          {certification}
        </p>
      )}
    </>
  );
}

/** The buyer's invitations. ONE CLICK — the address is on the record. */
function BuyerInvitations({
  buyerLeadId,
  canEdit,
  listingBySubmissionId,
}: {
  buyerLeadId: string;
  canEdit: boolean;
  listingBySubmissionId: Record<string, string | null | undefined>;
}) {
  const toast = useToast();
  const invites = useBuyerPrequalificationInvites(buyerLeadId);
  const send = useSendBuyerPrequalInvite(buyerLeadId);
  const resend = useResendBuyerPrequalInvite(buyerLeadId);
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false);

  async function onSend(confirmDuplicate = false) {
    try {
      await send.mutateAsync({ confirmDuplicate });
      setConfirmingDuplicate(false);
      toast.success("Invitation sent.");
    } catch (err) {
      if ((err as { status?: number })?.status === 409) {
        setConfirmingDuplicate(true);
        return;
      }
      toast.error(
        (err as { message?: string })?.message ?? "Could not send the invitation.");
    }
  }

  return (
    <InvitationsSection
      subject="buyer"
      canEdit={canEdit}
      rows={invites.data?.data ?? []}
      loading={invites.isLoading}
      error={invites.isError}
      retrying={invites.isFetching}
      onRetry={() => void invites.refetch()}
      resending={resend.isPending}
      onResend={(inviteId) =>
        resend.mutate(inviteId, {
          onSuccess: () => toast.success("Invitation resent."),
          onError: (err) =>
            toast.error(
              (err as { message?: string })?.message
                ?? "Could not resend the invitation."),
        })
      }
      composer={{
        trigger: (
          <OneClickInviteTrigger
            subject="buyer"
            pending={send.isPending}
            onSend={() => void onSend(false)}
          />
        ),
      }}
      duplicate={
        confirmingDuplicate && (
          <SubjectDuplicateConfirm
            subject="buyer"
            testId="prequal-invite-duplicate"
            pending={send.isPending}
            onConfirm={() => void onSend(true)}
            onCancel={() => setConfirmingDuplicate(false)}
          />
        )
      }
      rowContext={(invite) => (
        <InviteContext invite={invite} listingBySubmissionId={listingBySubmissionId} />
      )}
    />
  );
}

/** What this invitation was sent for, when we can say. The seller surface has
 *  no equivalent, which is why it is a slot rather than a shared field. */
function InviteContext({
  invite,
  listingBySubmissionId,
}: {
  invite: PrequalInviteDto;
  listingBySubmissionId: Record<string, string | null | undefined>;
}) {
  const listingName = invite.submissionId
    ? listingBySubmissionId[invite.submissionId]
    : null;

  // ⚠️ Only a COMPLETED invitation can be resolved this way, because only then
  // does the invite carry the `submissionId` the attempt is keyed on. An
  // outstanding invitation sent FROM a submission knows its source through
  // `sourceEntityType`/`sourceEntityId`, but turning that into a listing name
  // needs a join no read performs today — the pointer is on the wire, not the
  // resolved name. Marked as "from a submission" rather than faked.
  if (listingName) return <span>for {listingName}</span>;
  if (invite.sourceEntityId) return <span>from a submission</span>;
  return null;
}
